import type { GameState } from './types';
import { PROCRASTINATIONS, DETOURS } from './upgrades';

export const DEFAULT_STATE: GameState = {
  timeWasted: 0,
  totalTimeWasted: 0,
  lifetimeTimeWasted: 0,
  wisdom: 0,
  clicks: 0,
  epiphaniesCount: 0,
  lastSavedTime: 0,
  startDate: 0,
  currentRunStartTime: 0,
  sandAmount: 60,
  maxSandCapacity: 60,
  rotationDegrees: 0,
  procrastinations: {},
  detours: {},
  wisdomUpgrades: {},
  gameCompleted: false,
  hasSeenEndScreen: false,
  stats: {
    totalOfflineTimeWasted: 0,
    maxTimeWastedPassiveRate: 0,
    timeSpentActive: 0,
    achievementsUnlocked: []
  }
};

const LOCAL_STORAGE_KEY = 'wasting_time_save_v1';

export function createNewState(): GameState {
  const now = Date.now();
  return {
    ...JSON.parse(JSON.stringify(DEFAULT_STATE)),
    lastSavedTime: now,
    startDate: now,
    currentRunStartTime: now
  };
}

export function loadGameState(): GameState {
  try {
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!saved) {
      return createNewState();
    }
    const parsed = JSON.parse(saved);
    
    // Deep merge to ensure default structures exist
    const newState = createNewState();
    
    // Copy top level fields
    newState.timeWasted = Number(parsed.timeWasted) || 0;
    newState.totalTimeWasted = Number(parsed.totalTimeWasted) || 0;
    newState.lifetimeTimeWasted = Number(parsed.lifetimeTimeWasted) || 0;
    newState.wisdom = Number(parsed.wisdom) || 0;
    newState.clicks = Number(parsed.clicks) || 0;
    newState.epiphaniesCount = Number(parsed.epiphaniesCount) || 0;
    newState.startDate = Number(parsed.startDate) || newState.startDate;
    newState.currentRunStartTime = Number(parsed.currentRunStartTime) || newState.currentRunStartTime;
    newState.lastSavedTime = Number(parsed.lastSavedTime) || Date.now();
    newState.sandAmount = typeof parsed.sandAmount === 'number' ? parsed.sandAmount : 60;
    newState.maxSandCapacity = typeof parsed.maxSandCapacity === 'number' ? parsed.maxSandCapacity : 60;
    newState.rotationDegrees = typeof parsed.rotationDegrees === 'number' ? parsed.rotationDegrees : 0;
    newState.gameCompleted = !!parsed.gameCompleted;
    newState.hasSeenEndScreen = !!parsed.hasSeenEndScreen;
    
    // Merge dictionaries
    if (parsed.procrastinations && typeof parsed.procrastinations === 'object') {
      Object.keys(parsed.procrastinations).forEach(key => {
        newState.procrastinations[key] = Math.max(0, parseInt(parsed.procrastinations[key], 10) || 0);
      });
    }
    if (parsed.detours && typeof parsed.detours === 'object') {
      Object.keys(parsed.detours).forEach(key => {
        newState.detours[key] = Math.max(0, parseInt(parsed.detours[key], 10) || 0);
      });
    }
    if (parsed.wisdomUpgrades && typeof parsed.wisdomUpgrades === 'object') {
      Object.keys(parsed.wisdomUpgrades).forEach(key => {
        newState.wisdomUpgrades[key] = Math.max(0, parseInt(parsed.wisdomUpgrades[key], 10) || 0);
      });
    }

    // Merge stats
    if (parsed.stats && typeof parsed.stats === 'object') {
      newState.stats.totalOfflineTimeWasted = Number(parsed.stats.totalOfflineTimeWasted) || 0;
      newState.stats.maxTimeWastedPassiveRate = Number(parsed.stats.maxTimeWastedPassiveRate) || 0;
      newState.stats.timeSpentActive = Number(parsed.stats.timeSpentActive) || 0;
      if (Array.isArray(parsed.stats.achievementsUnlocked)) {
        newState.stats.achievementsUnlocked = parsed.stats.achievementsUnlocked.filter(
          (item: unknown) => typeof item === 'string'
        );
      }
    }
    
    return newState;
  } catch (e) {
    console.error('Failed to load game state, creating new.', e);
    return createNewState();
  }
}

export function saveGameState(state: GameState): void {
  try {
    state.lastSavedTime = Date.now();
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    console.error('Failed to save game state.', e);
  }
}

export function calculatePassiveRate(state: GameState): number {
  // Sum of bought procrastinations
  let baseRate = 0;
  PROCRASTINATIONS.forEach(proc => {
    const count = state.procrastinations[proc.id] || 0;
    baseRate += count * proc.baseRate;
  });

  // Prestige Wisdom Point boost: +2% per point
  const wisdomBonus = 1 + (state.wisdom * 0.02);

  // Wisdom Upgrades boosts
  const timeDilationLevel = state.wisdomUpgrades['time_dilation'] || 0;
  const dilationBonus = 1 + (timeDilationLevel * 0.10); // +10% per level

  return baseRate * wisdomBonus * dilationBonus;
}

export function calculateClickPower(state: GameState): number {
  // Base click power: 1s
  let baseClick = 1.0;
  
  DETOURS.forEach(detour => {
    const count = state.detours[detour.id] || 0;
    baseClick += count * detour.baseIncrement;
  });

  // Wisdom point multiplier: +2% per Wisdom point
  const wisdomBonus = 1 + (state.wisdom * 0.02);

  // Wisdom upgrade click boost
  const efficiencyLevel = state.wisdomUpgrades['subconscious_efficiency'] || 0;
  const efficiencyBonus = 1 + (efficiencyLevel * 0.15); // +15% per level

  return baseClick * wisdomBonus * efficiencyBonus;
}

export function calculateOfflineProgress(state: GameState): { elapsedSeconds: number; wastedOffline: number } | null {
  if (state.lastSavedTime <= 0) return null;
  
  const elapsedMs = Date.now() - state.lastSavedTime;
  const elapsedSeconds = elapsedMs / 1000;
  
  // Only trigger offline progress if away for more than 30 seconds
  if (elapsedSeconds < 30) return null;

  // Limit offline progress to 24 hours (86400 seconds)
  const maxOfflineSeconds = 86400; 
  const effectiveSeconds = Math.min(elapsedSeconds, maxOfflineSeconds);

  const passiveRate = calculatePassiveRate(state);
  const wastedOffline = passiveRate * effectiveSeconds;

  return {
    elapsedSeconds: Math.floor(elapsedSeconds),
    wastedOffline
  };
}
